// userService.ts

import api from "@/services/api";
import { type AxiosResponse } from "axios";

/**
 * Interface matching NestJS UserInfo schema attributes & profile extensions
 */
export interface UserInfo {
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  position?: string;
  phoneNumber?: string;
  location?: string;
  bio?: string;
  department?: string;
  website?: string;
  avatarUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UploadProfilePicResponse {
  message: string;
  imageId: string;
  filename: string;
}

export type UpdateUserInfoDto = Partial<UserInfo>;

class UserInfoService {
  /**
   * Fetch metadata profile credentials for the logged-in user
   * Endpoint: GET /v1/user-info
   */
  async getProfile(): Promise<UserInfo> {
    try {
      const response: AxiosResponse<UserInfo> = await api.get("/user-info");
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to fetch user profile:", error);
      if (error instanceof Error) {
        throw new Error(error.message || "Failed to load user profile.");
      }
      throw new Error("Failed to load user profile.");
    }
  }

  /**
   * Partially update or patch user metadata profile attributes
   * Endpoint: PATCH /v1/user-info
   */
  async updateProfile(updateDto: UpdateUserInfoDto): Promise<UserInfo> {
    try {
      const response: AxiosResponse<UserInfo> = await api.patch("/user-info", updateDto);
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to update user profile:", error);
      if (error instanceof Error) {
        throw new Error(error.message || "Failed to update profile details.");
      }
      throw new Error("Failed to update profile details.");
    }
  }

  /**
   * Upload or update profile picture for the logged-in user
   * Endpoint: POST /user-info/profile-pic
   */
  async uploadProfilePic(file: File): Promise<UploadProfilePicResponse> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response: AxiosResponse<UploadProfilePicResponse> = await api.post("/user-info/profile-pic", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to upload profile picture:", error);
      if (error instanceof Error) {
        throw new Error(error.message || "Failed to upload profile picture.");
      }
      throw new Error("Failed to upload profile picture.");
    }
  }

  /**
   * Fetch the profile picture as a Blob and return a local object URL
   * so it passes your JWT Authorization header correctly.
   */
  // async getProfilePicUrl(): Promise<string> {
  //   try {
  //     const response = await api.get("/user-info/profile-pic", {
  //       responseType: "blob",
  //     });
  //     const contentTypeHeader = response.headers["content-type"];
  //     const contentType = typeof contentTypeHeader === "string" ? contentTypeHeader : "image/jpeg";
  //     const blob = new Blob([response.data], { type: contentType });
  //     return URL.createObjectURL(blob);
  //   } catch (error: unknown) {
  //     console.error("Failed to load profile picture:", error);
  //     throw new Error("Failed to load profile picture.");
  //   }
  // }
  async getProfilePicUrl(): Promise<string> {
    try {
      const response = await api.get("/user-info/profile-pic", {
        responseType: "blob",
      });
      // Ensure the blob is not empty
      if (!response.data || response.data.size === 0) {
        throw new Error("Received empty image blob.");
      }
      const contentTypeHeader = response.headers["content-type"];
      const contentType = typeof contentTypeHeader === "string" ? contentTypeHeader : "image/jpeg";
      const blob = new Blob([response.data], { type: contentType });
      return URL.createObjectURL(blob);
    } catch (error: unknown) {
      console.error("Failed to load profile picture:", error);
      throw error;
    }
  }
}

// Create and export singleton instance
const userInfoService = new UserInfoService();
export default userInfoService;
